import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTemplate } from '@/contexts/TemplateContext';
import { NotifyMeButton } from './NotifyMeButton';

interface TimeSlot {
  time: string;       // "HH:mm" format
  end_time?: string;  // "HH:mm" format for display
  available: boolean;
  duration_minutes?: number;
  pricing_type?: 'standard' | 'peak' | 'off_peak';
  price_multiplier?: number;
}

interface TimeSlotSelectionProps {
  selectedSlot: string | null;
  availableSlots: TimeSlot[];
  isLoading: boolean;
  onSlotChange: (slot: string) => void;
  onSlotsRefresh: () => void;
  profileId?: string;
  date?: string | null;
  serviceIds?: string[];
}

export const TimeSlotSelection: React.FC<TimeSlotSelectionProps> = ({
  selectedSlot,
  availableSlots,
  isLoading,
  onSlotChange,
  onSlotsRefresh,
  profileId,
  date,
  serviceIds = [],
}) => {
  const { colors } = useTemplate();
  const { t } = useTranslation('booking');

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const availableTimes = availableSlots.filter((s) => s.available);
  const unavailableSlots = availableSlots.filter((s) => !s.available);

  return (
    <div className="bg-card rounded-lg shadow-sm border p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-foreground">{t('datetime.selectTimeSlot')}</h3>
        <button
          onClick={onSlotsRefresh}
          disabled={isLoading}
          className="text-sm px-3 py-1.5 rounded-md transition-colors disabled:opacity-50 hover:bg-muted"
          style={{ color: colors.colors.primary }}
        >
          {isLoading ? t('datetime.refreshing') : t('datetime.refresh')}
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div
            className="animate-spin rounded-full h-8 w-8 border-b-2"
            style={{ borderColor: colors.colors.primary }}
          />
        </div>
      ) : availableTimes.length === 0 ? (
        <div className="text-center py-8 space-y-4">
          <p className="text-muted-foreground mb-2">{t('datetime.noSlots')}</p>
          <p className="text-sm text-muted-foreground">{t('datetime.noSlotsHint')}</p>
          {profileId && date && unavailableSlots[0] && (
            <NotifyMeButton
              profileId={profileId}
              date={date}
              time={unavailableSlots[0].time}
              serviceIds={serviceIds}
            />
          )}
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {availableSlots.map((slot) => {
            const isPeak = slot.pricing_type === 'peak';
            const isOffPeak = slot.pricing_type === 'off_peak';
            const isDisabled = !slot.available;

            const peakTooltip = isPeak ? `Peak pricing: +${((slot.price_multiplier ?? 1) - 1) * 100}%` : '';
            const offPeakTooltip = isOffPeak ? `Off-peak discount: ${(1 - (slot.price_multiplier ?? 1)) * 100}% off` : '';

            return (
              <div key={slot.time} className="space-y-1">
                <button
                  type="button"
                  onClick={() => slot.available && onSlotChange(slot.time)}
                  disabled={isDisabled}
                  title={isPeak ? peakTooltip : isOffPeak ? offPeakTooltip : undefined}
                  className={`w-full p-3 rounded-lg text-center transition-colors border-2 ${
                    selectedSlot === slot.time
                      ? 'text-white'
                      : slot.available
                      ? `bg-muted text-foreground hover:bg-muted/70 ${isPeak ? 'border-orange-400 bg-orange-50' : ''} ${isOffPeak ? 'border-green-400 bg-green-50' : ''}`
                      : 'bg-muted/50 text-muted-foreground opacity-50 cursor-not-allowed'
                  }`}
                  style={
                    selectedSlot === slot.time
                      ? { backgroundColor: colors.colors.primary }
                      : slot.available
                      ? { backgroundColor: 'var(--background)', color: colors.colors.text }
                      : undefined
                  }
                >
                  <div className="font-medium">
                    {isPeak && <span className="mr-1">🔥</span>}
                    {isOffPeak && <span className="mr-1">❄️</span>}
                    {formatTime(slot.time)}
                  </div>
                  {slot.duration_minutes && (
                    <div className="text-xs mt-1 opacity-70">{slot.duration_minutes} min</div>
                  )}
                </button>
                {!slot.available && profileId && date && (
                  <NotifyMeButton
                    profileId={profileId}
                    date={date}
                    time={slot.time}
                    serviceIds={serviceIds}
                    compact
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {availableTimes.length > 0 && !isLoading && (
        <p className="text-sm text-muted-foreground mt-4 text-center">
          {t('datetime.slotsAvailable', { count: availableTimes.length })}
          {availableTimes.filter(s => s.pricing_type === 'peak').length > 0 && (
            <span className="text-orange-600 ml-2">
              🔥 {availableTimes.filter(s => s.pricing_type === 'peak').length} peak
            </span>
          )}
          {availableTimes.filter(s => s.pricing_type === 'off_peak').length > 0 && (
            <span className="text-green-600 ml-2">
              ❄️ {availableTimes.filter(s => s.pricing_type === 'off_peak').length} off-peak
            </span>
          )}
        </p>
      )}
    </div>
  );
};
